// JSON-path completion emission for the IntellisenseEngine.
//
// Slice C4 of `Ralph/JsonColumnSemantics/PRD.md`. When the cursor sits inside
// a JSON-path string position (after `->'`, `->>'`, `#>'`, `JSON_VALUE(col, '`,
// `JSON_QUERY(col, '`, `JSON_EXISTS(col, '`, or `json_extract(col, '`), the
// resolver pulls path hints from the per-session sample cache + MetaInfo
// `pathHints` and emits them as tier-6 completion candidates.
//
// The position detector is engine-agnostic at the token level — every
// dialect's JSON path extraction routes through one of the recognised
// invocation forms. Per-engine resolvers call `findPrecedingColumnOperand`
// to identify *which* JSON column the path is being addressed against, then
// pass the resulting classification + column ref to this emitter.

import type { CompletionCandidate } from '@/core/engines';
import type { SqlEngine } from '@/features/sql-grammar/model';
import type { SampledPath } from '@/features/sql-resolver/json/pathHints';
import { mergeWithDeclaredHints } from '@/features/sql-resolver/json/pathHints';
import type { PathHintsCacheEntry } from '@/features/sql-resolver/json/pathHints';

export type EmitJsonPathCandidatesArgs = {
  readonly cacheEntry: PathHintsCacheEntry | undefined;
  readonly declaredHints: readonly string[] | undefined;
  /**
   * Maximum candidates to emit. PRD JCS-9 caps at 64; consumers may pass a
   * lower number if the surrounding completion list is tight on budget.
   */
  readonly maxCandidates?: number;
  /**
   * The path text already typed inside the string literal (from
   * `JsonPathStringPosition.partial`). Labels are always the full path token
   * (`$.customer_id`), but `insertText` is emitted RELATIVE to this prefix so
   * accepting a suggestion produces valid SQL rather than doubling the prefix
   * — at `JSON_EXTRACT(c, '$.▮')` the label is `$.customer_id` while the
   * insertText is `customer_id`. Defaults to '' (empty slot → insert the full
   * path).
   */
  readonly partial?: string;
  /**
   * When the merged hint set is empty, emit the lone syntactic root `$`
   * instead of nothing. Off by default so regular function-form slots keep
   * falling through to normal completion on a cold cache. Callers opt in only
   * for slots where the root is *always* a valid starting target even with no
   * sampled/declared paths — the JSON_TABLE root path arg and per-column
   * `PATH '…'` literals (relative to the synthetic row). Only honoured for
   * full-form (`jsonPath`) slots.
   */
  readonly offerSyntacticRootWhenEmpty?: boolean;
  /**
   * The shape of the surrounding path-string position. Selects how each
   * stored `$.foo.bar` cache path is formatted as a candidate label:
   *
   *   - `'jsonPath'` (default) — emit full `$.foo.bar` paths verbatim.
   *     Right for function-form invocations (`JSON_VALUE(col, '$.…')`,
   *     `JSON_EXTRACT`, `JSON_QUERY`, …).
   *   - `'arrow'` — emit bare top-level key names (`foo`), deduped. Right
   *     for the SQL single-level operators `col -> 'key'` / `col ->> 'key'`
   *     across pgsql / mysql / sqlite where only the next key segment is
   *     valid syntax inside the string literal.
   *   - `'pgPathArray'` — emit bare segment names. Right for PG
   *     `col #> '{a,b}'` / `col #>> '{a,b}'` where the literal is a
   *     comma-separated array of segments.
   *
   * When omitted, defaults to `'jsonPath'` so legacy callers (no position
   * detector) keep the historical full-path behaviour.
   */
  readonly positionKind?: JsonPathStringPositionKind;
  /**
   * The engine the candidates are emitted for. Optional so call sites compile
   * before they're updated to forward it. Currently only gates the synthesized
   * array-element `[*]` wildcard prefixes: MSSQL's `JSON_VALUE` / `JSON_QUERY`
   * reject the `[*]` wildcard (only `OPENJSON` supports it) and return scalars
   * only, so on `engine === 'mssql'` we suppress `[*]`-bearing candidates.
   * Numeric `[N]` index paths are accepted by MSSQL and kept.
   */
  readonly engine?: SqlEngine | undefined;
};

/**
 * Emit JSON path candidates into the completion list. Returns the number of
 * candidates emitted so callers can short-circuit subsequent emission paths
 * (e.g. don't emit the regular string-literal hints when path hints
 * already covered the position).
 */
export function emitJsonPathCandidates(
  out: CompletionCandidate[],
  args: EmitJsonPathCandidatesArgs,
): number {
  const merged = mergeWithDeclaredHints(args.cacheEntry, args.declaredHints);
  const kind: JsonPathStringPositionKind = args.positionKind ?? 'jsonPath';
  // `jsonPath` is the function-form slot family — `JSON_EXTRACT(col, '$.…')`,
  // `JSON_VALUE`, `JSON_QUERY`, `JSON_EXISTS`, `JSON_TABLE(col, '$…')`, and
  // per-column `PATH '$.…'`. There the string literal must be a COMPLETE
  // JSONPath, so labels carry the leading `$.` verbatim and the syntactic
  // root `$` is always a valid target (offered even when the structure cache
  // is empty). The bare operator forms (`col -> 'key'`, `col #> '{a,b}'`)
  // accept only the next key/segment, so those collapse to bare tokens.
  //
  // ENGINE DIVERGENCE (documented at the site per the parity rule): MySQL's
  // `->` / `->>` are NOT key operators — they are shorthand for
  // `JSON_EXTRACT(col, path)` / `JSON_UNQUOTE(JSON_EXTRACT(col, path))`, so
  // the string literal must be a COMPLETE path expression. `metadata ->
  // 'status'` raises "Invalid JSON path expression"; only `metadata ->
  // '$.status'` runs. Emitting the bare key there suggested SQL the server
  // rejects (mysql coverage script.13 §2/§3 pin the `$.`-prefixed set).
  // PG keeps the bare form — `col -> 'key'` takes a key, and `'$.key'` would
  // look up a literal key named `$.key`. SQLite accepts BOTH spellings, so it
  // stays on the bare form its rows recorded.
  const fullForm = kind === 'jsonPath' || (kind === 'arrow' && args.engine === 'mysql');
  // Nothing to offer with an empty hint set — UNLESS the caller opted into the
  // syntactic-root fallback (JSON_TABLE root / per-column `PATH '…'`), where
  // the lone `$` is a valid starting target even on a cold cache. Otherwise a
  // bare-cache slot keeps falling through to normal completion rather than
  // short-circuiting on a lone `$`.
  if (merged.length === 0) {
    if (fullForm && args.offerSyntacticRootWhenEmpty) {
      out.push(pathToCandidate({ path: '$', frequency: 0 }, args.partial ?? ''));
      return 1;
    }
    return 0;
  }
  const cap = Math.max(0, args.maxCandidates ?? 64);
  // F30: MSSQL `JSON_VALUE` / `JSON_QUERY` reject the `[*]` wildcard (only
  // OPENJSON supports it). Drop every `[*]`-bearing path BEFORE prefix
  // synthesis: removing them from the input also prevents `[*]` prefixes from
  // being derived (the synthesizer only walks `base`), so no second filter is
  // needed. Numeric `[N]` index paths stay — MSSQL accepts those. Other engines
  // keep `[*]` unchanged.
  const base = args.engine === 'mssql' ? merged.filter(p => !p.path.includes('[*]')) : merged;
  const expanded = withIntermediatePrefixes(base);
  const shaped = fullForm ? withSyntacticRoot(expanded) : stripPrefix(expanded);
  const formatted = shaped.sort(
    (a, b) => b.frequency - a.frequency || a.path.localeCompare(b.path),
  );
  // Dedupe by label (synthesized prefixes / the root can collide with a
  // sampled path) and drop the empty-string label the bare `$` root strips to
  // in operator-form. Keep the first occurrence — the sort already placed the
  // highest-frequency entry first.
  const seen = new Set<string>();
  const slice: SampledPath[] = [];
  for (const p of formatted) {
    if (p.path === '' || seen.has(p.path)) continue;
    seen.add(p.path);
    slice.push(p);
    if (slice.length >= cap) break;
  }
  const partial = args.partial ?? '';
  for (const p of slice) {
    out.push(pathToCandidate(p, partial));
  }
  return slice.length;
}

/**
 * Synthesize every INTERMEDIATE path a sampled leaf navigates through. The
 * sampler records only the deepest observed leaf, but each container it walks
 * past is itself a valid completion target, so a caret at the tier above the
 * leaf would otherwise see nothing to select.
 *
 * Two shapes are synthesized, both at frequency 0 (so real sampled paths keep
 * ranking ahead of them) and both skipped when the path is already present:
 *
 *   - array element — the prefix ending just after a `]` with more path
 *     following (`$.items[*].sku` → `$.items[*]`). Valid as
 *     `jsonb_path_query(col, '$.items[*]')` / `JSON_QUERY(col, '$.items[*]')`.
 *   - object parent — the prefix ending just before a `.` that is not the
 *     root's own separator (`$.address.city` → `$.address`). Valid as
 *     `data ->> '$.address'` / `json_extract(data, '$.address')`, which
 *     returns the sub-object. Without it a structure cache that only ever
 *     recorded leaves (`$.address.city`, `$.address.country`) offered
 *     `address.city` / `address.country` at the root tier of an operator-form
 *     slot but never the `address` key the user has to type first
 *     (sqlite script.58 §1/§3).
 *
 * The root prefix itself (`$`) is NOT synthesized here — full-form slots add
 * it via `withSyntacticRoot` and operator-form slots strip it to the empty
 * label, which the dedup pass drops.
 */
function withIntermediatePrefixes(paths: readonly SampledPath[]): SampledPath[] {
  // F48: `paths` is hard-capped at SAMPLE_PATH_CAP (= 64) short paths upstream
  // (pathHintsCache `.slice(0, SAMPLE_PATH_CAP)`), so this Map+sort+dedup is
  // O(≤64) per keystroke — small enough that a memo cache isn't worth the
  // identity-key risk; intentionally recomputed.
  const byPath = new Map<string, number>();
  for (const p of paths) {
    if (!byPath.has(p.path)) byPath.set(p.path, p.frequency);
  }
  for (const p of paths) {
    for (let i = 0; i < p.path.length; i += 1) {
      if (p.path[i] === ']' && i + 1 < p.path.length) {
        const prefix = p.path.slice(0, i + 1);
        if (!byPath.has(prefix)) byPath.set(prefix, 0);
      }
      // Object parent. `i > 1` excludes the root separator of `$.foo` (whose
      // prefix is the bare `$`); every deeper dot names a real container.
      if (p.path[i] === '.' && i > 1) {
        const prefix = p.path.slice(0, i);
        if (!byPath.has(prefix)) byPath.set(prefix, 0);
      }
    }
  }
  return [...byPath.entries()].map(([path, frequency]) => ({ path, frequency }));
}

/**
 * Function-form slots take a complete JSONPath, so paths keep their `$.`
 * prefix verbatim. Prepend the syntactic root `$` (a valid target even with an
 * empty structure cache — e.g. `JSON_TABLE(col, '$' COLUMNS(…))`). The root
 * carries frequency 0 so populated sampled paths still rank ahead of it; it is
 * only emitted once (deduped downstream against any sampled `$`).
 */
function withSyntacticRoot(paths: readonly SampledPath[]): SampledPath[] {
  return [{ path: '$', frequency: 0 }, ...paths];
}

/**
 * Strip the leading `$.` / `$` prefix from each path so labels read as the
 * raw path tokens (`customer_id`, `items[*].sku`, `shipping.city`). Paths
 * without a prefix are passed through untouched.
 */
function stripPrefix(paths: readonly SampledPath[]): SampledPath[] {
  return paths.map(p => ({
    path: trimDollarPrefix(p.path),
    frequency: p.frequency,
  }));
}

function trimDollarPrefix(path: string): string {
  if (path.startsWith('$.')) return path.slice(2);
  if (path.startsWith('$')) return path.slice(1);
  return path;
}

function pathToCandidate(p: SampledPath, partial: string): CompletionCandidate {
  // `label` / `filterText` carry the full path token so the suggest widget
  // shows `$.customer_id` and Monaco's fuzzy match operates on it. `insertText`
  // is emitted relative to the already-typed `partial`: when the typed prefix
  // is a prefix of this path we insert only the remaining suffix, so accepting
  // at `'$.▮'` yields `'$.customer_id'` (not `'$.$.customer_id'`). The resolver
  // detects we are *inside* a string and there are no surrounding quotes.
  const insertText =
    partial.length > 0 && p.path.startsWith(partial) ? p.path.slice(partial.length) : p.path;
  return {
    label: p.path,
    insertText,
    isSnippet: false,
    kind: 'snippet',
    tier: 6,
    detail: p.frequency > 0 ? `frequency: ${p.frequency}` : 'declared',
    filterText: p.path,
  };
}

/**
 * Detect whether the cursor sits inside a recognised JSON-path string
 * position. Returns the start offset of the in-progress path string + the
 * partial path text typed so far, or null when the cursor isn't in a
 * JSON-path string.
 *
 * Heuristic: walk back from `offset` until we hit an unescaped `'` (start
 * of string). If the characters immediately before the opening quote match
 * a recognised JSON-extracting operator/function, we are in a path string.
 *
 * Recognised prefixes (case-insensitive on the function names):
 *   `->`, `->>`, `#>`, `#>>`
 *   `JSON_VALUE(<col>, `, `JSON_QUERY(<col>, `, `JSON_EXISTS(<col>, `
 *   `JSON_MODIFY(<col>, `, `JSON_PATH_EXISTS(<col>, `
 *   `JSON_EXTRACT(<col>, `, `JSON_CONTAINS_PATH(<col>, ?, `
 *   `json_extract(<col>, ` (sqlite)
 *
 * The detector returns the column-side text so the caller can resolve the
 * operand via `findPrecedingColumnOperand`.
 */
/**
 * The "shape" of the JSON-path string position — selects label formatting
 * downstream. Different operator/function families expect different syntax
 * inside the string literal:
 *   - `arrow`        — `col -> 'key'` / `col ->> 'key'`: bare top-level key
 *                      names; deep paths collapse to their first segment.
 *   - `pgPathArray`  — `col #> '{a,b,c}'` / `col #>> '{a,b,c}'`: PG path-array
 *                      literal, emit bare key names (no `$.` prefix).
 *   - `jsonPath`     — `JSON_VALUE(col, '$.a.b')` / `JSON_EXTRACT(col, '$.a.b')`
 *                      and other function-form: full `$.path` syntax.
 */
export type JsonPathStringPositionKind = 'arrow' | 'pgPathArray' | 'jsonPath';

export type JsonPathStringPosition = {
  /** Start offset of the partial path body (just after the opening `'`). */
  readonly bodyStart: number;
  /** The partial path text typed so far (may be empty). */
  readonly partial: string;
  /**
   * The operator/function family that opened this path-string position.
   * Drives label formatting in `emitJsonPathCandidates`.
   */
  readonly kind: JsonPathStringPositionKind;
  /**
   * The text segment between the start of the relevant LHS column reference
   * and the position of the opening string quote. Callers feed this into
   * `findPrecedingColumnOperand` (which already does the column resolution
   * + classification lookup) by passing this segment's end offset.
   */
  readonly columnSideEnd: number;
  /**
   * True for slots where the syntactic root `$` is a valid starting target
   * even with an empty structure cache — the JSON_TABLE root path arg and
   * per-column `PATH '…'` literals. Callers forward this to
   * `emitJsonPathCandidates`'s `offerSyntacticRootWhenEmpty`.
   */
  readonly rootFallback?: boolean;
  /**
   * True for `PATH '…'` literals inside a `JSON_TABLE … COLUMNS(…)` body: the
   * path is relative to the synthetic row, so there is no LHS column to
   * resolve. Callers skip column resolution and emit the root-fallback set.
   */
  readonly noColumn?: boolean;
};

export function findJsonPathStringPosition(
  text: string,
  offset: number,
): JsonPathStringPosition | undefined {
  if (offset < 0 || offset > text.length) return undefined;
  // Walk back to the start of the in-progress string. We track unescaped
  // single quotes only — SQL escapes are `''` (doubled), so we count
  // consecutive quotes.
  let i = offset - 1;
  let quoteStart = -1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '\n') return undefined; // strings don't span newlines in v1
    if (ch === "'") {
      // Lookahead: doubled `''` is escaped; treat as content.
      if (text[i - 1] === "'") {
        i -= 2;
        continue;
      }
      quoteStart = i;
      break;
    }
    i--;
  }
  if (quoteStart < 0) return undefined;
  const partial = text.slice(quoteStart + 1, offset);
  // Inspect characters before the opening quote, skipping whitespace.
  let j = quoteStart - 1;
  while (j >= 0 && /\s/.test(text[j])) j--;
  if (j < 0) return undefined;
  // Operator-form prefixes: `->`, `->>`, `#>`, `#>>` end with `>` at index `j`.
  if (text[j] === '>') {
    const tail = text.slice(Math.max(0, j - 2), j + 1);
    const arrow =
      tail.endsWith('->') ||
      tail.endsWith('->>');
    const pgPath = !arrow && (tail.endsWith('#>') || tail.endsWith('#>>'));
    if (arrow || pgPath) {
      // F29: derive `columnSideEnd` from the ACTUAL matched operator length, not
      // the fixed 3-char `tail` window. The 3-char forms (`->>`/`#>>`) end with
      // `>>`; the 2-char forms (`->`/`#>`) end with a single `>`. The operator's
      // first char is the exclusive end of the column identifier — using
      // `tail.length` (always 3) overshoots by one for the 2-char operators (no
      // whitespace), truncating the column name read downstream.
      const opLen = text[j - 1] === '>' ? 3 : 2;
      const operatorStart = j - (opLen - 1);
      return {
        bodyStart: quoteStart + 1,
        partial,
        columnSideEnd: operatorStart,
        kind: arrow ? 'arrow' : 'pgPathArray',
      };
    }
  }
  // Function-form prefixes: `, ` then the function-call's column ref.
  if (text[j] === ',') {
    // Look back across the column ref to find the opening paren and function name.
    let k = j - 1;
    let parenDepth = 0;
    while (k >= 0) {
      const c = text[k];
      if (c === ')') parenDepth++;
      else if (c === '(') {
        if (parenDepth === 0) break;
        parenDepth--;
      }
      k--;
    }
    if (k < 0) return undefined;
    // Walk back over the function identifier.
    let f = k - 1;
    while (f >= 0 && /\s/.test(text[f])) f--;
    const idEnd = f + 1;
    while (f >= 0 && /[A-Za-z0-9_]/.test(text[f])) f--;
    const fnName = text.slice(f + 1, idEnd).toUpperCase();
    if (PATH_FN_NAMES.has(fnName)) {
      // JSON_TABLE's root path arg is a valid `$` / `$[*]` target even when the
      // column has no structure cache yet, so flag the root fallback.
      const rootFallback = fnName === 'JSON_TABLE';
      return {
        bodyStart: quoteStart + 1,
        partial,
        columnSideEnd: j,
        kind: 'jsonPath',
        ...(rootFallback ? { rootFallback: true } : {}),
      };
    }
  }
  // Per-column `PATH '…'` literal inside a `JSON_TABLE … COLUMNS(…)` body. The
  // path is relative to the synthetic row produced by the JSON_TABLE root, so
  // there is no LHS column to resolve — offer at least the syntactic root.
  if (/[A-Za-z]/.test(text[j])) {
    let p = j;
    while (p >= 0 && /[A-Za-z]/.test(text[p])) p--;
    if (text.slice(p + 1, j + 1).toUpperCase() === 'PATH') {
      return {
        bodyStart: quoteStart + 1,
        partial,
        columnSideEnd: p + 1,
        kind: 'jsonPath',
        rootFallback: true,
        noColumn: true,
      };
    }
  }
  return undefined;
}

const PATH_FN_NAMES: ReadonlySet<string> = new Set([
  'JSON_VALUE',
  'JSON_QUERY',
  'JSON_EXISTS',
  'JSON_MODIFY',
  'JSON_PATH_EXISTS',
  'JSON_EXTRACT',
  'JSON_CONTAINS',
  'JSON_CONTAINS_PATH',
  'JSONB_PATH_EXISTS',
  'JSONB_PATH_QUERY',
  // mysql 8 / oracle 12c+ relational projection — root-path arg sits in the
  // same comma-prefix slot as the function-form helpers above.
  'JSON_TABLE',
]);
